import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { FiBox, FiShoppingBag, FiDollarSign, FiStar, FiPlus, FiChevronRight } from 'react-icons/fi';
import { getVendorStats } from '../../services/api';
import VendorLayout from '../../components/layout/VendorLayout';
import './VendorDashboard.css';


const STAT_CARDS = [
  { key: 'totalProducts', label: 'Total Products', icon: <FiBox size={20} />, color: '#6C47FF', bg: '#ede9ff' },
  { key: 'totalOrders',   label: 'Total Orders',   icon: <FiShoppingBag size={20} />, color: '#00D4AA', bg: '#e0faf5' },
  { key: 'totalRevenue',  label: 'Revenue (Rs. )',     icon: <FiDollarSign size={20} />, color: '#22c55e', bg: '#dcfce7', prefix: 'Rs. ' },
  { key: 'rating',        label: 'Avg Rating',      icon: <FiStar size={20} />, color: '#f59e0b', bg: '#fef3c7', suffix: ' ★' },
];

const STATUS_CONFIG = {
  placed:     { cls: 'badge-info' },
  confirmed:  { cls: 'badge-primary' },
  processing: { cls: 'badge-warning' },
  shipped:    { cls: 'badge-accent' },
  delivered:  { cls: 'badge-success' },
  cancelled:  { cls: 'badge-danger' },
};

export default function VendorDashboard() {
  const [stats, setStats] = useState(null);
  const [recentOrders, setRecentOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchStats = useCallback(async () => {
    try {
      const { data } = await getVendorStats();
      setStats(data.stats);
      setRecentOrders(data.recentOrders || []);
    } catch (err) {
      console.error('Vendor dashboard error:', err?.response?.data || err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchStats(); }, [fetchStats]);

  const formatValue = (card, val) => {
    if (!val && val !== 0) return '—';
    const prefix = card.prefix || '';
    const suffix = card.suffix || '';
    if (card.key === 'totalRevenue') return `${prefix}${Number(val).toLocaleString('en-PK')}`;
    if (card.key === 'rating') return `${Number(val).toFixed(1)}${suffix}`;
    return `${prefix}${val}${suffix}`;
  };

  return (
    <VendorLayout
      title="Dashboard"
      action={
        <Link to="/vendor/products/add" className="btn btn-primary">
          <FiPlus size={15} /> Add Product
        </Link>
      }
    >
      <div className="vdash">
        {/* Stat cards */}
        <div className="vdash-stats">
          {STAT_CARDS.map((card) => (
            <div key={card.key} className="vstat-card card">
              <div className="vstat-icon" style={{ background: card.bg, color: card.color }}>
                {card.icon}
              </div>
              <div className="vstat-info">
                {loading ? (
                  <div className="vstat-skeleton" />
                ) : (
                  <p className="vstat-value">{formatValue(card, stats?.[card.key])}</p>
                )}
                <p className="vstat-label">{card.label}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Recent orders */}
        <div className="vdash-section">
          <div className="vdash-section-header">
            <h2 className="vdash-section-title">Recent Orders</h2>
            <Link to="/vendor/orders" className="vsee-all">
              View All <FiChevronRight size={14} />
            </Link>
          </div>

          {loading ? (
            <div className="loading-center"><div className="spinner" /></div>
          ) : recentOrders.length === 0 ? (
            <div className="empty-state" style={{ padding: '2rem' }}>
              <div className="empty-state-icon" style={{ fontSize: '2rem' }}>📦</div>
              <h3>No orders yet</h3>
              <p>Your orders will appear here once buyers purchase your products.</p>
            </div>
          ) : (
            <div className="table-wrapper">
              <table>
                <thead>
                  <tr>
                    <th>Order #</th>
                    <th>Buyer</th>
                    <th>Items</th>
                    <th>Total</th>
                    <th>Status</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {recentOrders.map((order) => {
                    const { cls } = STATUS_CONFIG[order.orderStatus] || { cls: 'badge-info' };
                    const date = new Date(order.createdAt).toLocaleDateString('en-PK', { day: 'numeric', month: 'short' });
                    return (
                      <tr key={order._id}>
                        <td>
                          <p className="order-num-cell">{order.orderNumber}</p>
                          <p className="order-date-cell">{date}</p>
                        </td>
                        <td className="font-medium">{order.buyer?.name || '—'}</td>
                        <td className="text-muted text-sm">
                          {order.items?.slice(0, 2).map((i) => i.name || i.product?.name || '—').join(', ')}
                          {order.items?.length > 2 && ` +${order.items.length - 2}`}
                        </td>
                        <td className="font-semi">Rs. {Number(order.total).toFixed(2)}</td>
                        <td><span className={`badge ${cls}`}>{order.orderStatus}</span></td>
                        <td>
                          <Link to={`/orders/${order._id}`} className="btn btn-ghost btn-sm">View</Link>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Quick actions */}
        <div className="vdash-section">
          <h2 className="vdash-section-title">Quick Actions</h2>
          <div className="vquick-actions">
            <Link to="/vendor/products/add" className="vquick-card">
              <span className="vquick-icon" style={{ background: '#ede9ff', color: '#6C47FF' }}><FiPlus size={20} /></span>
              <span className="vquick-label">Add Product</span>
            </Link>
            <Link to="/vendor/products" className="vquick-card">
              <span className="vquick-icon" style={{ background: '#e0faf5', color: '#00D4AA' }}><FiBox size={20} /></span>
              <span className="vquick-label">Manage Products</span>
            </Link>
            <Link to="/vendor/orders" className="vquick-card">
              <span className="vquick-icon" style={{ background: '#fef3c7', color: '#f59e0b' }}><FiShoppingBag size={20} /></span>
              <span className="vquick-label">View Orders</span>
            </Link>
            <Link to="/vendor/settings" className="vquick-card">
              <span className="vquick-icon" style={{ background: '#dcfce7', color: '#22c55e' }}><FiStar size={20} /></span>
              <span className="vquick-label">Store Settings</span>
            </Link>
          </div>
        </div>
      </div>
    </VendorLayout>
  );
}
